// The high-school window: during the school season, the hours schools rent on
// their practice days (Mon–Thu 2:00–5:30 by default) are reserved for high
// schools as a block. Members are turned away from it, except the tiers in
// season.windowTiers (Gold by default), whose bookings wait for an admin's approval.
//
// Computed from settings/schoolSchedule rather than stored as reservations, so
// it blocks no court on its own: the courts schools actually use are still the
// "School use" reservations, and a Gold request for an empty court is possible.
//
// NOTE ON DUPLICATION: functions/schoolWindow.js is a copy of this file for the
// server (CRA can't import from functions/, functions can't import ESM from
// src/). createMemberBooking decides; the client only shows the block. Change
// both together.

export const WINDOW_DEFAULTS = { days: [1, 2, 3, 4], windowStart: '14:00', windowEnd: '17:30' };
export const WINDOW_TITLE = 'Reserved for high schools';

// '14:00' → '2:00 PM'.
export function clock12(t) {
  const [h, m] = String(t || '').split(':').map(Number);
  if (!Number.isFinite(h)) return '';
  return `${((h + 11) % 12) + 1}:${String(m || 0).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
}

const dayOf = (date) => new Date(`${date}T12:00:00`).getDay();

// First and last date the window applies. season.start/end when set; otherwise
// first practice through the last regional date on the FHSAA calendar.
export function windowRange(settings) {
  const season = (settings && settings.season) || {};
  const cal = (settings && settings.calendar) || {};
  const start = season.start || cal.firstPractice || '';
  const end = season.end || cal.regionals3 || cal.regionals2 || cal.regionals1 || cal.districtsEnd || cal.lastGamesEnd || '';
  return start && end && start <= end ? { start, end } : null;
}

export function windowSpec(settings) {
  const season = (settings && settings.season) || {};
  return {
    days: Array.isArray(season.days) && season.days.length ? season.days : WINDOW_DEFAULTS.days,
    windowStart: season.windowStart || WINDOW_DEFAULTS.windowStart,
    windowEnd: season.windowEnd || WINDOW_DEFAULTS.windowEnd,
  };
}

// Whether members of this tier may ask for window time. season.windowTiers
// lists tier ids when set; until then, any tier named Gold (annual and monthly
// are separate tiers with generated ids).
export function tierMayRequestWindow(settings, tier) {
  if (!tier) return false;
  const ids = settings && settings.season && settings.season.windowTiers;
  if (Array.isArray(ids)) return ids.includes(tier.id);
  return /\bgold\b/i.test(String(tier.name || ''));
}

// Whether the window is in force on this date. Spring break is out: no school
// practices, so the courts go back to everyone.
export function windowOnDate(settings, date) {
  const range = windowRange(settings);
  if (!range || !date || date < range.start || date > range.end) return false;
  const cal = (settings && settings.calendar) || {};
  if (cal.springBreakStart && date >= cal.springBreakStart && date <= (cal.springBreakEnd || cal.springBreakStart)) return false;
  return windowSpec(settings).days.includes(dayOf(date));
}

// Whether a booking from startTime to endTime on date touches the window.
// Times are 'HH:MM', so string comparison orders them.
export function overlapsWindow(settings, date, startTime, endTime) {
  if (!windowOnDate(settings, date)) return false;
  const { windowStart, windowEnd } = windowSpec(settings);
  return startTime < windowEnd && windowStart < endTime;
}

// Every date the window is in force, for drawing it on the calendar.
export function windowDates(settings) {
  const range = windowRange(settings);
  if (!range) return [];
  const out = [];
  const d = new Date(`${range.start}T12:00:00`);
  for (let i = 0; i < 400; i++) {
    const s = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (s > range.end) break;
    if (windowOnDate(settings, s)) out.push(s);
    d.setDate(d.getDate() + 1);
  }
  return out;
}

// The window as FullCalendar background events: one labelled block across the
// given courts on each school day of the season.
export function windowEvents(settings, courtIds) {
  if (!settings) return [];
  const w = windowSpec(settings);
  return windowDates(settings).map(d => ({
    id: `school-window-${d}`,
    resourceIds: courtIds,
    start: `${d}T${w.windowStart}`,
    end: `${d}T${w.windowEnd}`,
    title: WINDOW_TITLE,
    display: 'background',
    backgroundColor: '#B0BEC5',
  }));
}
